import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * useProctoring â€” behavioural integrity monitoring for school exams.
 *
 * Props:
 *   schoolExamId       {string}
 *   proctoringSettings {object}
 *   token              {string}
 *   schoolId           {string}
 *   onBlock            {function}
 *   initialViolationCount {number} restores counters across reloads
 *
 * Returns:
 *   warningLevel, violationCount, showWarningBanner, dismissWarning,
 *   isFullscreen, requestFullscreen, flush,
 *   requestScreenCapture, screenCaptureActive
 */
export default function useProctoring({
    schoolExamId,
    proctoringSettings = {},
    token,
    schoolId,
    onBlock,
    initialViolationCount = 0,
}) {
    const enabled = !!proctoringSettings?.enabled;

    // State
    const [warningLevel, setWarningLevel]           = useState(() => computeLevel(initialViolationCount));
    const [violationCount, setViolationCount]       = useState(initialViolationCount);
    const [showWarningBanner, setShowWarningBanner] = useState(initialViolationCount > 0);
    const [isFullscreen, setIsFullscreen]           = useState(!!document.fullscreenElement);
    const [screenCaptureActive, setScreenCaptureActive] = useState(false);

    // Refs
    const buffer             = useRef([]);
    const totalRef           = useRef(initialViolationCount);
    const flushing           = useRef(false);
    const blockedRef         = useRef(false);
    const settingsRef        = useRef(proctoringSettings);
    const pendingScreenshots = useRef({});
    const screenshotIdRef    = useRef(0);
    const screenStreamRef    = useRef(null);  // MediaStream from getDisplayMedia
    const screenVideoRef     = useRef(null);  // hidden <video> element playing the screen stream

    useEffect(() => { settingsRef.current = proctoringSettings; }, [proctoringSettings]);

    // Safety net: if a reload landed exactly at/after the block threshold (e.g. the
    // flush that would have triggered onBlock never completed before the page unloaded),
    // finish the block instead of silently letting the student continue past the limit.
    useEffect(() => {
        if (!enabled || blockedRef.current) return;
        const settings = settingsRef.current || {};
        const max = settings.maxViolations || 20;
        if (settings.blockOnMaxViolations && initialViolationCount >= max) {
            blockedRef.current = true;
            if (typeof onBlock === 'function') onBlock();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //Risk level — a function declaration (not const) so it's hoisted and usable in the initial state above
    function computeLevel(count) {
        if (count >= 5) return 3;
        if (count >= 3) return 2;
        if (count >= 1) return 1;
        return 0;
    }

    //Request full-screen capture via getDisplayMedia
    // Must be called from a user-gesture handler (button click).
    // Returns true on success, false if denied or unsupported.
    const requestScreenCapture = useCallback(async () => {
        if (!navigator.mediaDevices?.getDisplayMedia) return false;
        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({
                video: { displaySurface: 'monitor', frameRate: 1 },
                audio: false,
            });
            // Reject if student shared a window or browser tab instead of the entire screen
            const track = stream.getVideoTracks()[0];
            const surfaceType = track?.getSettings?.()?.displaySurface;
            if (surfaceType && surfaceType !== 'monitor') {
                stream.getTracks().forEach(t => t.stop());
                return 'wrong_surface';
            }
            screenStreamRef.current = stream;

            // Attach to a hidden video element so drawImage() works reliably even
            // when the browser tab loses focus (ImageCapture.grabFrame() throws in
            // that case, causing a silent fall-through to html2canvas).
            const video = document.createElement('video');
            video.srcObject = stream;
            video.muted = true;
            video.style.cssText =
                'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;' +
                'opacity:0;pointer-events:none;z-index:-9999;';
            document.body.appendChild(video);
            // Wait for metadata so videoWidth/videoHeight are known before first capture
            await new Promise(resolve => {
                if (video.readyState >= 1) resolve();
                else video.addEventListener('loadedmetadata', resolve, { once: true });
            });
            try { await video.play(); } catch (_) { /* autoplay policy — frames still available */ }
            screenVideoRef.current = video;

            setScreenCaptureActive(true);

            // Clean up when the student stops sharing mid-exam
            const cleanup = () => {
                screenStreamRef.current = null;
                if (screenVideoRef.current) {
                    screenVideoRef.current.srcObject = null;
                    try { screenVideoRef.current.remove(); } catch (_) {}
                    screenVideoRef.current = null;
                }
                setScreenCaptureActive(false);
            };
            track?.addEventListener('ended', cleanup);
            return true;
        } catch (_) {
            return false;
        }
    }, []);

    // Stop screen capture stream
    const stopScreenCapture = useCallback(() => {
        if (screenStreamRef.current) {
            screenStreamRef.current.getTracks().forEach(t => t.stop());
            screenStreamRef.current = null;
        }
        if (screenVideoRef.current) {
            screenVideoRef.current.srcObject = null;
            try { screenVideoRef.current.remove(); } catch (_) {}
            screenVideoRef.current = null;
        }
        setScreenCaptureActive(false);
    }, []);

    // Stop stream on unmount
    useEffect(() => {
        return () => { stopScreenCapture(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Capture screenshot and upload
    // Prefers a live screen-share stream (full desktop); falls back to html2canvas.
    const captureAndUpload = useCallback(async (type) => {
        try {
            let blob = null;

            if (screenVideoRef.current) {
                // Path 1: draw frame from the persistent screen-share video element.
                // drawImage() is synchronous and works even when the tab is backgrounded,
                // unlike ImageCapture.grabFrame() which throws when the tab loses focus
                // and silently falls through to html2canvas (browser DOM only).
                const video = screenVideoRef.current;
                if (video.videoWidth > 0 && video.videoHeight > 0) {
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width  = Math.round(video.videoWidth  / 2);
                        canvas.height = Math.round(video.videoHeight / 2);
                        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
                        blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.65));
                    } catch (_) {
                        // drawImage failed - fall through to html2canvas
                    }
                }
            }

            if (!blob) {
                // Path 2: html2canvas fallback (captures browser tab only)
                const html2canvas = (await import('html2canvas')).default;
                const canvas = await html2canvas(document.body, {
                    scale: 0.5,
                    useCORS: true,
                    logging: false,
                    allowTaint: true,
                    ignoreElements: (el) =>
                        el.getAttribute && el.getAttribute('data-proctor-ui') === 'true',
                });
                blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.6));
            }

            if (!blob) return null;

            const formData = new FormData();
            formData.append('screenshot', blob, `screenshot_${type}_${Date.now()}.jpg`);
            if (schoolExamId) formData.append('examId', schoolExamId);

            const headers = { 'x-access-token': token };
            if (schoolId) headers['x-school-id'] = schoolId;

            const res = await fetch('/student/upload/screenshot', {
                method: 'POST',
                headers,
                body: formData,
            });
            if (!res.ok) return null;
            const { url } = await res.json();
            return url || null;
        } catch (_) {
            return null;
        }
    }, [token, schoolId, schoolExamId]);

    // Flush buffer to backend
    const flush = useCallback(async () => {
        if (!enabled || flushing.current || !buffer.current.length) return;
        const batch = buffer.current.splice(0);
        if (!batch.length) return;

        flushing.current = true;
        try {
            // Attach resolved screenshot URLs before sending
            for (const entry of batch) {
                const entryId = entry._sid;
                if (entryId !== undefined && pendingScreenshots.current[entryId]) {
                    try {
                        const url = await pendingScreenshots.current[entryId];
                        if (url) entry.screenshotUrl = url;
                    } catch (_) {}
                    delete pendingScreenshots.current[entryId];
                }
            }

            // Strip internal tracking field
            const sendBatch = batch.map(({ _sid: _, ...rest }) => rest);

            const headers = { 'Content-Type': 'application/json', 'x-access-token': token };
            if (schoolId) headers['x-school-id'] = schoolId;

            await fetch(`/student/exams/${schoolExamId}/proctor-event`, {
                method:  'POST',
                headers,
                body:    JSON.stringify({ violations: sendBatch }),
            });
        } catch (_) {
            // Silently drop â€” exam must not be interrupted
        } finally {
            flushing.current = false;
        }
    }, [enabled, token, schoolId, schoolExamId]);

    // ── Record a violation
    // Human-readable detail map so the teacher sees useful descriptions.
    const DETAIL_MAP = {
        tab_switch:      'Student navigated away from the exam tab',
        window_blur:     'Browser window lost focus (possible switch to another app)',
        copy_attempt:    'Attempted to copy content from the exam',
        paste_attempt:   'Attempted to paste content into the exam',
        dev_tools:       'Developer tools were opened',
        right_click:     'Right-clicked on the exam page',
        fullscreen_exit: 'Exited required fullscreen mode',
        mouse_leave:     'Mouse cursor left the exam window',
        auto_submit:     'Exam auto-submitted â€” maximum violation limit reached',
    };

    const record = useCallback((type, detail = '', highSeverity = false) => {
        if (!enabled) return;

        const entryId = ++screenshotIdRef.current;
        // Use provided detail or fall back to the human-readable map
        const resolvedDetail = detail || DETAIL_MAP[type] || type;
        const entry = { _sid: entryId, type, timestamp: new Date().toISOString(), detail: resolvedDetail };
        buffer.current.push(entry);
        totalRef.current += 1;

        const newTotal = totalRef.current;
        setViolationCount(newTotal);
        setWarningLevel(computeLevel(newTotal));
        setShowWarningBanner(true);

        // Start screenshot capture in background
        pendingScreenshots.current[entryId] = captureAndUpload(type);

        // Block check
        const settings = settingsRef.current || {};
        const max = settings.maxViolations || 20;
        if (settings.blockOnMaxViolations && newTotal >= max && !blockedRef.current) {
            blockedRef.current = true;
            // Push auto_submit marker so the backend flags the attempt
            const autoId = ++screenshotIdRef.current;
            buffer.current.push({
                _sid: autoId,
                type: 'auto_submit',
                timestamp: new Date().toISOString(),
                detail: DETAIL_MAP.auto_submit,
            });
            // Give screenshot ~900 ms to finish, then flush + trigger onBlock
            setTimeout(() => {
                flush();
                if (typeof onBlock === 'function') onBlock();
            }, 900);
            return;
        }

        if (highSeverity) {
            setTimeout(flush, 900);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, captureAndUpload, flush]);

    // Event listeners 
    useEffect(() => {
        if (!enabled) return;

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') record('tab_switch', '', true);
        };
        const onBlur = () => record('window_blur', '', false);
        const onCopy = (e) => { e.preventDefault(); record('copy_attempt', '', false); };
        const onPaste = (e) => { e.preventDefault(); record('paste_attempt', '', false); };
        const onContextMenu = (e) => { e.preventDefault(); record('right_click', '', false); };
        const onMouseLeave = () => record('mouse_leave', '', false);

        const onKeyDown = (e) => {
            const ctrl  = e.ctrlKey || e.metaKey;
            const shift = e.shiftKey;
            if (
                e.key === 'F12' ||
                (ctrl && shift && ['I', 'J', 'C'].includes(e.key.toUpperCase())) ||
                (ctrl && e.key.toUpperCase() === 'U')
            ) {
                e.preventDefault();
                record('dev_tools', `Key: ${e.key}`, true);
                return;
            }
            if (ctrl && (e.key === 'c' || e.key === 'x')) { record('copy_attempt', '', false); return; }
            if (ctrl && e.key === 'v') record('paste_attempt', '', false);
        };

        const onFullscreenChange = () => {
            const inFs = !!document.fullscreenElement;
            setIsFullscreen(inFs);
            if (!inFs && settingsRef.current?.requireFullscreen) record('fullscreen_exit', '', true);
        };

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('blur', onBlur);
        document.addEventListener('copy', onCopy, { capture: true });
        document.addEventListener('paste', onPaste, { capture: true });
        document.addEventListener('contextmenu', onContextMenu);
        document.addEventListener('keydown', onKeyDown, { capture: true });
        document.addEventListener('mouseleave', onMouseLeave);
        document.addEventListener('fullscreenchange', onFullscreenChange);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('blur', onBlur);
            document.removeEventListener('copy', onCopy, { capture: true });
            document.removeEventListener('paste', onPaste, { capture: true });
            document.removeEventListener('contextmenu', onContextMenu);
            document.removeEventListener('keydown', onKeyDown, { capture: true });
            document.removeEventListener('mouseleave', onMouseLeave);
            document.removeEventListener('fullscreenchange', onFullscreenChange);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    // Periodic flush 
    useEffect(() => {
        if (!enabled) return;
        const id = setInterval(flush, 30_000);
        return () => clearInterval(id);
    }, [enabled, flush]);

    // Final flush on unmount
    useEffect(() => {
        return () => { flush(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fullscreen helpers
    const requestFullscreen = useCallback(async () => {
        try {
            await document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } catch (_) {}
    }, []);

    const dismissWarning = useCallback(() => {
        if (warningLevel < 3) setShowWarningBanner(false);
    }, [warningLevel]);

    return {
        warningLevel,
        violationCount,
        showWarningBanner,
        dismissWarning,
        isFullscreen,
        requestFullscreen,
        flush,
        requestScreenCapture,
        screenCaptureActive,
    };
}
